import {
    type AreaValue,
    areaValueForRow,
} from '@apps/web/features/coverage/model/area_model';
import {
    cellAt,
    isOpenState,
} from '@apps/web/features/coverage/model/cell_model';
import {
    type CoverageFilters,
    type CoverageRow,
    DAY_INDEXES,
    DAY_PARTS,
    type DayPartKey,
} from '@apps/web/features/coverage/model/coverage_types';

function matchesCellSelection(
    row: CoverageRow,
    filters: CoverageFilters,
): boolean {
    if (filters.days.length === 0 && filters.dayParts.length === 0) {
        return true;
    }
    // An unselected dimension means "at any time" / "on any day"; only an
    // explicit selection is combined strictly. "Day: Tuesday" alone keeps
    // anyone with at least one open window that Tuesday.
    const days = filters.days.length > 0 ? filters.days : DAY_INDEXES;
    const parts: ReadonlyArray<DayPartKey> =
        filters.dayParts.length > 0
            ? filters.dayParts
            : DAY_PARTS.map((part) => part.key);
    if (filters.dayParts.length === 0) {
        // Days-only: each day counts when ANY of its windows is open; the
        // match mode combines across days. Tue-morning + Wed-evening must
        // satisfy an every-mode "Tue and Wed" selection.
        const dayOpenAtAnyTime = (day: number) =>
            parts.some((part) => isOpenState(cellAt(row, day, part).state));
        return filters.matchMode === 'any'
            ? days.some(dayOpenAtAnyTime)
            : days.every(dayOpenAtAnyTime);
    }
    const combineDays =
        filters.days.length === 0 || filters.matchMode === 'any'
            ? 'some'
            : 'every';
    const dayMatches = (part: DayPartKey) =>
        combineDays === 'some'
            ? days.some((day) => isOpenState(cellAt(row, day, part).state))
            : days.every((day) => isOpenState(cellAt(row, day, part).state));
    return parts.every(dayMatches);
}

export function filterRows(
    rows: ReadonlyArray<CoverageRow>,
    filters: CoverageFilters,
): Array<CoverageRow> {
    const query = filters.search.trim().toLowerCase();
    return rows.filter((row) => {
        if (query && !row.name.toLowerCase().includes(query)) {
            return false;
        }
        if (
            filters.offices.length > 0 &&
            !filters.offices.includes(areaValueForRow(row, 'office').key)
        ) {
            return false;
        }
        if (
            filters.cities.length > 0 &&
            !filters.cities.includes(areaValueForRow(row, 'city').key)
        ) {
            return false;
        }
        if (
            filters.zips.length > 0 &&
            !filters.zips.includes(areaValueForRow(row, 'zip').key)
        ) {
            return false;
        }
        if (
            filters.employmentTypes.length > 0 &&
            !filters.employmentTypes.includes(row.employment_type ?? '')
        ) {
            return false;
        }
        if (
            filters.tags.length > 0 &&
            !filters.tags.every((tag) => row.tags.includes(tag))
        ) {
            return false;
        }
        if (
            filters.availability === 'unknown_only' &&
            row.has_availability_data
        ) {
            return false;
        }
        return matchesCellSelection(row, filters);
    });
}

export type FacetOptions = {
    readonly offices: ReadonlyArray<AreaValue>;
    readonly cities: ReadonlyArray<AreaValue>;
    readonly zips: ReadonlyArray<AreaValue>;
    readonly tags: ReadonlyArray<string>;
    readonly employmentTypes: ReadonlyArray<string>;
};

/**
 * Facet options come from the FULL row set (not the filtered one) so
 * options never vanish while narrowing.
 */
export function buildFacetOptions(
    rows: ReadonlyArray<CoverageRow>,
): FacetOptions {
    const offices = new Map<string, AreaValue>();
    const cities = new Map<string, AreaValue>();
    const zips = new Map<string, AreaValue>();
    const tags = new Set<string>();
    const employmentTypes = new Set<string>();
    for (const row of rows) {
        const office = areaValueForRow(row, 'office');
        const city = areaValueForRow(row, 'city');
        const zip = areaValueForRow(row, 'zip');
        offices.set(office.key, office);
        cities.set(city.key, city);
        zips.set(zip.key, zip);
        if (row.employment_type) {
            employmentTypes.add(row.employment_type);
        }
        for (const tag of row.tags) {
            tags.add(tag);
        }
    }
    const sorted = (values: Set<string>) =>
        [...values].sort((a, b) => a.localeCompare(b));
    const sortedAreas = (values: Map<string, AreaValue>) =>
        [...values.values()].sort(
            (a, b) =>
                a.label.localeCompare(b.label) || a.key.localeCompare(b.key),
        );
    return {
        offices: sortedAreas(offices),
        cities: sortedAreas(cities),
        zips: sortedAreas(zips),
        tags: sorted(tags),
        employmentTypes: sorted(employmentTypes),
    };
}
